
const API_URL = process.env.REACT_APP_NOCODB_URL;
const API_TOKEN = process.env.REACT_APP_NOCODB_API_TOKEN;

if (!API_URL || !API_TOKEN) {
  throw new Error("NocoDB URL and API Token must be defined in your .env file");
}

const headers = {
  "Content-Type": "application/json",
  "xc-token": API_TOKEN,
};

// Define interfaces for our data structures
export interface Book {
  Id: number;
  title: string;
  author: string;
  cover_url?: string;
  publication_year?: number;
  CreatedAt?: string;
}

export interface ReadingLog {
  Id: number;
  notes: string;
  CreatedAt?: string;
  book_id: number; // Foreign key to the Book
  _nc_m2m_reading_log_books?: Array<{
    books_id: number;
    reading_log_id: number;
    books: Book;
  }>;
}

// Generic fetch function
async function apiFetch(endpoint: string, options: RequestInit = {}) {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "An API error occurred");
  }
  return response.json();
}

// --- Books API ---
export const getBooks = (): Promise<{ list: Book[] }> => {
  return apiFetch("/api/v1/db/data/v1/pdemv2tfdeuyr3s/books");
};

export const createBook = (book: { title: string; author: string; cover_url?: string; publication_year?: number }): Promise<Book> => {
  return apiFetch("/api/v1/db/data/v1/pdemv2tfdeuyr3s/books", {
    method: "POST",
    body: JSON.stringify(book),
  });
};

export const deleteBook = (bookId: number): Promise<void> => {
  return apiFetch(`/api/v1/db/data/v1/pdemv2tfdeuyr3s/books/${bookId}`, {
    method: "DELETE",
  });
};

// --- Reading Logs API ---
export const getReadingLogs = (): Promise<{ list: ReadingLog[] }> => {
  return apiFetch("/api/v1/db/data/v1/pdemv2tfdeuyr3s/reading_logs?nested[book_id]=true");
};

// Helper functions to inspect database structure
export const inspectTableStructure = async (tableName: string) => {
  try {
    const response = await apiFetch(`/api/v1/db/meta/projects/pdemv2tfdeuyr3s/tables?includeM2M=true`);
    console.log("All tables:", response);
    
    // Find the specific table
    const table = response.list?.find((t: any) => t.table_name === tableName || t.title === tableName);
    if (table) {
      console.log(`Table ${tableName} structure:`, table);
      
      // Get detailed column info
      const columnsResponse = await apiFetch(`/api/v1/db/meta/projects/pdemv2tfdeuyr3s/tables/${table.id}/columns`);
      console.log(`Columns for ${tableName}:`, columnsResponse);
      
      return { table, columns: columnsResponse.list };
    }
  } catch (error) {
    console.error("Failed to inspect table structure:", error);
  }
};

export const inspectReadingLogStructure = () => inspectTableStructure('reading_logs');
export const inspectBooksStructure = () => inspectTableStructure('books');

// Test API endpoints to see what actually exists
export const testApiConnections = async () => {
  console.log("🧪 Testing API connections...");
  
  // Test 1: List all tables to see what's available
  try {
    const tables = await apiFetch("/api/v1/db/meta/projects/pdemv2tfdeuyr3s/tables");
    console.log("✅ Available tables:", tables.list?.map((t: any) => ({ name: t.table_name, title: t.title, id: t.id })));
    
    // Test 2: Check if we can get existing reading logs
    try {
      const logs = await apiFetch("/api/v1/db/data/v1/pdemv2tfdeuyr3s/reading_logs");
      console.log("✅ Reading logs endpoint works. Sample log:", logs.list?.[0]);
    } catch (error) {
      console.log("❌ Reading logs endpoint failed:", error);
    }
    
    // Test 3: Check if we can get books
    try {
      const books = await apiFetch("/api/v1/db/data/v1/pdemv2tfdeuyr3s/books");
      console.log("✅ Books endpoint works. Sample book:", books.list?.[0]);
    } catch (error) {
      console.log("❌ Books endpoint failed:", error);
    }
    
  } catch (error) {
    console.log("❌ Failed to get tables:", error);
  }
};

export const createReadingLog = async (log: { book_id: number; notes: string }): Promise<ReadingLog> => {
  console.log("🚀 Creating reading log with relationship...");
  
  // Create the reading log first (without book_id as it's handled through M2M table)
  const newLog = await apiFetch("/api/v1/db/data/v1/pdemv2tfdeuyr3s/reading_logs", {
    method: "POST",
    body: JSON.stringify({
      notes: log.notes
    }),
  });
  
  console.log("📝 Created reading log:", newLog.Id);
  
  // Create the many-to-many relationship directly in the M2M table
  try {
    console.log("🔗 Creating many-to-many relationship...");
    await apiFetch("/api/v1/db/data/v1/pdemv2tfdeuyr3s/_nc_m2m_reading_log_books", {
      method: "POST",
      body: JSON.stringify({
        books_id: log.book_id,
        reading_log_id: newLog.Id
      }),
    });
    console.log("✅ Successfully created book relationship");
    return newLog;
  } catch (error) {
    console.error("❌ M2M relationship failed:", error);
    
    // If relationship creation fails, delete the orphaned log
    console.error("❌ Deleting orphaned log...");
    try {
      await deleteReadingLog(newLog.Id);
      console.log("🗑️ Deleted orphaned log");
    } catch (deleteError) {
      console.error("❌ Failed to delete orphaned log:", deleteError);
    }
    
    throw new Error("Failed to create reading log with book relationship. M2M relationship creation failed.");
  }
};

export const deleteReadingLog = (logId: number): Promise<void> => {
  return apiFetch(`/api/v1/db/data/v1/pdemv2tfdeuyr3s/reading_logs/${logId}`, {
    method: "DELETE",
  });
};

export const deleteMultipleReadingLogs = (logIds: number[]): Promise<void> => {
  const deletePromises = logIds.map(id => deleteReadingLog(id));
  return Promise.all(deletePromises).then(() => {});
};

// Cleanup function to remove orphaned reading logs (those without book relationships)
export const cleanupOrphanedReadingLogs = async (): Promise<{ deletedCount: number; deletedLogs: ReadingLog[] }> => {
  try {
    const response = await getReadingLogs();
    const allLogs = response.list || [];
    
    // Only consider logs older than 30 seconds to avoid cleaning up newly created logs
    const thirtySecondsAgo = new Date(Date.now() - 30 * 1000);
    
    // Find logs that don't have any book relationship and are older than 30 seconds
    const orphanedLogs = allLogs.filter(log => {
      // Check if log has no book relationship data
      const hasNoRelationship = !log._nc_m2m_reading_log_books || 
                                log._nc_m2m_reading_log_books.length === 0 ||
                                !log._nc_m2m_reading_log_books[0]?.books;
      
      // Only include if it's older than 30 seconds
      const isOld = log.CreatedAt && new Date(log.CreatedAt) < thirtySecondsAgo;
      
      return hasNoRelationship && isOld;
    });
    
    console.log(`Found ${orphanedLogs.length} orphaned reading logs (older than 30 seconds):`, orphanedLogs);
    
    if (orphanedLogs.length === 0) {
      return { deletedCount: 0, deletedLogs: [] };
    }
    
    // Delete orphaned logs
    const orphanedIds = orphanedLogs.map(log => log.Id);
    await deleteMultipleReadingLogs(orphanedIds);
    
    console.log(`Successfully deleted ${orphanedLogs.length} orphaned reading logs`);
    return { deletedCount: orphanedLogs.length, deletedLogs: orphanedLogs };
  } catch (error) {
    console.error("Failed to cleanup orphaned reading logs:", error);
    throw error;
  }
};
